/**
 * LayersPanel Component
 *
 * Full-featured layers panel using SlidePanel (mode="view"). Shows the basemap,
 * artifact, standard and custom layer groups in collapsible sections.
 *
 * Single component for BOTH worlds:
 * - Authenticated (no `config`): admin "Add layer" + "Export GeoJSON" actions,
 *   country-scoped standard layers, editable custom layers (`LayerItem`).
 * - Public share (`config` provided): read-only, scoped to the share —
 *   no actions, artifact/standard groups filtered by the share config, custom
 *   layers rendered as plain read-only checkboxes. The header, toolbar (title
 *   left, resize + close right) and groups are identical to the authenticated
 *   panel, just non-editable.
 *
 * Data + per-mode branching live in {@link useLayersPanelData}.
 *
 * @module components/layers/LayersPanel
 */
import { memo, useState } from 'react';
import { Button, Checkbox, Group, Text } from '@mantine/core';
import { Download, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { SlidePanel } from '@/components/ui/SlidePanel/SlidePanel';
import { FormPanelTitle } from '@/components/common/form';
import type { PublicShareConfig } from '@/types/share';
import { LayerGroupSection } from '../LayerGroupSection/LayerGroupSection';
import { LayerCheckboxList } from '../LayerCheckboxList/LayerCheckboxList';
import { BasemapSwitcher } from '../BasemapSwitcher/BasemapSwitcher';
import { ThreeDBasemapNotice } from '../ThreeDBasemapNotice';
import { LayerItem } from '../LayerItem/LayerItem';
import { useLayersPanelData } from './hooks/useLayersPanelData';
import classes from './LayersPanel.module.css';

interface LayersPanelProps {
  /**
   * Public-share config. When provided the panel renders read-only and scoped
   * to the share; when omitted it renders the authenticated, editable panel.
   */
  config?: PublicShareConfig;
}

export const LayersPanel = memo(function LayersPanel({
  config,
}: LayersPanelProps) {
  const { t } = useTranslation();
  const {
    isPublic,
    isLayersExpanded,
    closeLayersPanel,
    isLayerConfiguratorOpen,
    isAdmin,
    visibleBasemapCount,
    artifactLayers,
    artifactLayerVisibility,
    toggleArtifactLayer,
    hasHiddenArtifactLayers,
    standardLayers,
    standardLayerVisibility,
    toggleStandardLayer,
    customLayers,
    customCount,
    showCustomGroup,
    customLayerVisibility,
    toggleCustomLayerVisibility,
    layersIsLoading,
    layersHasLoaded,
    isExporting,
    handleAddLayer,
    handleExportGeoJson,
    handleEdit,
    handleDelete,
    handleDownload,
  } = useLayersPanelData(config);

  // Collapse state
  const [basemapCollapsed, setBasemapCollapsed] = useState(false);
  const [artifactCollapsed, setArtifactCollapsed] = useState(true);
  const [standardCollapsed, setStandardCollapsed] = useState(true);
  const [customCollapsed, setCustomCollapsed] = useState(true);

  // Custom group: read-only checkboxes (public) or editable LayerItems (authed).
  const customRows = config ? (
    config.sharedCustomLayers.map((layer) => (
      <div key={layer.id} className={classes.standardItem}>
        <Checkbox
          checked={customLayerVisibility[layer.id] ?? true}
          onChange={() => toggleCustomLayerVisibility(layer.id)}
          label={layer.name}
        />
      </div>
    ))
  ) : customLayers.length === 0 && (layersIsLoading || !layersHasLoaded) ? (
    <Text size="sm" c="dimmed" className={classes.empty}>
      {t('common.loading')}
    </Text>
  ) : customLayers.length === 0 ? (
    <Text size="sm" c="dimmed" className={classes.empty}>
      {t('layers.empty')}
    </Text>
  ) : (
    customLayers.map((layer) => (
      <LayerItem
        key={layer.id}
        layer={layer}
        isVisible={customLayerVisibility[layer.id] ?? layer.isVisible}
        onToggle={() => toggleCustomLayerVisibility(layer.id)}
        isAdmin={isAdmin}
        onEdit={() => handleEdit(layer.id)}
        onDelete={() => handleDelete(layer.id, layer.name)}
        onDownload={() => handleDownload(layer.id)}
      />
    ))
  );

  return (
    <SlidePanel
      opened={isLayersExpanded}
      onClose={closeLayersPanel}
      headerTitle={<FormPanelTitle>{t('layers.title')}</FormPanelTitle>}
      isHidden={isLayerConfiguratorOpen}
      allowMinimize={false}
      defaultFullscreen={false}
      fullscreenWideContent
      translationPrefix="layers"
    >
      {!isPublic && (
        <Group gap="xs" className={classes.actions}>
          {isAdmin && (
            <Button
              size="xs"
              variant="light"
              leftSection={<Plus size={14} />}
              onClick={handleAddLayer}
            >
              {t('layers.actions.addLayer')}
            </Button>
          )}
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            leftSection={<Download size={14} />}
            onClick={handleExportGeoJson}
            loading={isExporting}
          >
            {t('workspace.actions.exportGeoJson')}
          </Button>
        </Group>
      )}

      <AppScrollArea fill fullBleedGutter="md">
        <div className={classes.content}>
          {/* Show the basemap group whenever at least one basemap is available,
            so the active basemap stays visible even with a single option. */}
          {visibleBasemapCount >= 1 && (
            <LayerGroupSection
              title={t('layers.groups.basemap')}
              isCollapsed={basemapCollapsed}
              onToggle={() => setBasemapCollapsed((prev) => !prev)}
              count={visibleBasemapCount}
            >
              <ThreeDBasemapNotice />
              <BasemapSwitcher />
            </LayerGroupSection>
          )}

          {artifactLayers.length > 0 && (
            <LayerGroupSection
              title={t('layers.groups.artifacts')}
              isCollapsed={artifactCollapsed}
              onToggle={() => setArtifactCollapsed((prev) => !prev)}
              count={artifactLayers.length}
              showHiddenIndicator={hasHiddenArtifactLayers}
            >
              <LayerCheckboxList
                items={artifactLayers}
                visibility={artifactLayerVisibility}
                onToggle={toggleArtifactLayer}
                rowClassName={classes.standardItem}
              />
            </LayerGroupSection>
          )}

          {/* Standard layers are country/share-scoped; hide the whole group
            when none are available. */}
          {standardLayers.length > 0 && (
            <LayerGroupSection
              title={t('layers.groups.standard')}
              isCollapsed={standardCollapsed}
              onToggle={() => setStandardCollapsed((prev) => !prev)}
              count={standardLayers.length}
            >
              <LayerCheckboxList
                items={standardLayers}
                visibility={standardLayerVisibility}
                onToggle={toggleStandardLayer}
                rowClassName={classes.standardItem}
              />
            </LayerGroupSection>
          )}

          {showCustomGroup && (
            <LayerGroupSection
              title={t('layers.groups.custom')}
              isCollapsed={customCollapsed}
              onToggle={() => setCustomCollapsed((prev) => !prev)}
              count={customCount}
            >
              {customRows}
            </LayerGroupSection>
          )}
        </div>
      </AppScrollArea>
    </SlidePanel>
  );
});
